import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getComicsByCategory, comicCategories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const [activeCategory, setActiveCategory] = useState(currentSlug);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = comicCategories.find(c => c.slug === activeCategory);
  const categoryName = currentCategory?.name || '全部';

  let comics = getComicsByCategory(activeCategory);

  if (sortBy === 'popular') {
    comics = [...comics].sort((a, b) => parseFloat(b.views.replace('亿', '')) - parseFloat(a.views.replace('亿', '')));
  } else if (sortBy === 'rating') {
    comics = [...comics].sort((a, b) => b.rating - a.rating);
  } else {
    comics = [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory, sortBy]);

  const categoryPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - 51漫画`,
    description: `${categoryName}漫画大全，51漫画在线观看，免费${categoryName}漫画全集在线阅读、下拉式阅读、实时阅读。`,
    url: `https://51manhua.com/category/${activeCategory}`,
    inLanguage: 'zh-CN',
  };

  const sortOptions = [
    { value: 'latest' as const, label: '最新' },
    { value: 'popular' as const, label: '人气' },
    { value: 'rating' as const, label: '评分' },
  ];

  return (
    <>
      <SEOHead
        title={`${categoryName}漫画`}
        description={`${categoryName}漫画大全，51漫画在线观看，免费${categoryName}漫画全集在线阅读、下拉式阅读、实时阅读，支持网页观看与在线漫画。`}
        keywords={`51漫画在线观看,51漫画在线,${categoryName}漫画,免费漫画,在线阅读,下拉式漫画`}
        canonicalUrl={`https://51manhua.com/category/${activeCategory}`}
        schemaObjects={[categoryPageSchema]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '漫画分类', to: '/category/all' },
                { label: categoryName },
              ]}
            />

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-6 mb-2">
              {categoryName}漫画
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              共找到 <strong className="text-primary-500">{comics.length}</strong> 部{categoryName !== '全部' ? categoryName : ''}漫画
            </p>

            {/* Category Tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {comicCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sort Bar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Comic Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
              {comics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 51漫画在线观看`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                        comic.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
                      }`}>
                        {comic.status}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                    {comic.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-accent-500"><i className="ri-star-fill text-xs mr-0.5"></i>{comic.rating}</span>
                    <span className="text-xs text-foreground-400">共{comic.chapters}话</span>
                  </div>
                </Link>
              ))}
            </div>

            {comics.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}